<template>
  <nav
    class="navbar-section flex align-items-center justify-content-between p-2 h-full"
  >
    <!-- Toggle sidebar button -->
    <Button
      v-if="layout == 'admin'"
      class="md:hidden"
      icon="pi pi-align-left"
      @click="store.commit('setShowSidebar', !store.getters.showSidebar)"
    />

    <div class="flex align-items-center gap-5 md:pr-5">
      <!-- Logo section -->
      <div class="hidden md:block logo-container">
        <div class="logo">
          <router-link to="/informes" class="block w-full h-full" />
        </div>
      </div>

      <!-- App logo and impersonation button -->
      <div
        v-auto-animate
        class="flex flex-column md:flex-row md:align-items-center md:gap-3"
        :class="[
          layout == 'admin' ? 'align-items-center' : 'align-items-start',
        ]"
      >
        <router-link to="/informes" class="no-underline">
          <h1 class="m-0 text-white">CUADROS DE MANDO</h1>
        </router-link>
        <Button
          v-if="showSelectedImpersonation"
          :label="`Impersonando a ${selectedImpersonation.label}`"
          icon="pi pi-times"
          class="impersonation-button"
          @click="clearImpersonation()"
        />
      </div>
    </div>

    <Button
      v-if="showGoLoginButton"
      label="Iniciar sesión"
      @click="handleGoLogin()"
    />

    <TheUserMenu
      v-if="showTheUserMenu && !closingSession"
      class="the-user-menu"
      :role="state$$.roles"
    >
      <!-- If user is admin, show the "change panel" action -->
      <template #info>
        <template v-if="showAdminFunctions">
          <div @click="changeLayout()" class="change-panel">
            <i class="pi pi-external-link"></i>
            {{ changePanelText }}
          </div>

          <AutoComplete
            v-model="selectedImpersonation"
            :suggestions="impersionationApi"
            @complete="(event) => getImpersonationUsers(event)"
            placeholder="Impersonar usuario"
            optionLabel="label"
            class="impersonation-dropdown"
            appendTo="self"
            forceSelection
            :delay="600"
            :disabled="showSelectedImpersonation"
          >
            <template #option="slotProps">
              <input hidden :value="slotProps.option.idUser" />
              <div class="flex flex-column">
                <span
                  ><b>{{ slotProps.option.label }}</b></span
                >
                <span>({{ slotProps.option.email }})</span>
              </div>
            </template>
          </AutoComplete>
        </template>

        <div class="flex justify-content-end">
          <Button
            size="small"
            class="mt-2"
            @click="clearAuthData({ manualLogout: true })"
            label="Cerrar sesión"
            severity="secondary"
          />
        </div>
      </template>
    </TheUserMenu>

    <ExternalLogedUser v-if="showExternalLoggedUser" />
  </nav>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import Button from "primevue/button";
import AutoComplete from "primevue/autocomplete";
import { TheUserMenu, useAuthorization } from "@sttedra_ui/framework";

import {
  isLogged,
  clearAuthData,
  getMsalToken,
} from "@/composables/_commons/auth.js";
import { appUserListAll } from "@/api/users/users.api.js";
import AppViewState from "@/pages/_root/app.view-state";
import ExternalLogedUser from "@/components/partials/ExternalLogedUser.vue";
// #region Props, Emits
// #endregion

// #region Params, Ref, Reactives, Computed
const state = new AppViewState();
const state$$ = reactive(state);
const store = useStore();
const { modelRef: authorizationModelRef } = useAuthorization();
const router = useRouter();

const triggerImpersonationUser = computed(
  () => store.getters.triggerImpersonationUser
);
const layout = computed(() => store.getters.layout);
const closingSession = computed(() => store.getters.closingSession);

const changePanelText = computed(() =>
  layout.value == "admin" ? "Modo usuario" : "Modo administrador"
);
const showGoLoginButton = computed(() => {
  if (userLogged.value.email) {
    return false;
  }

  const currentRoute = router.currentRoute.value;

  if (currentRoute.name == "PublicReport" || currentRoute.name == "Login") {
    return false;
  }

  return true;
});
const showAdminFunctions = computed(() => {
  return selectedImpersonation.value != null &&
    typeof selectedImpersonation.value === "object"
    ? selectedImpersonation.value.esAdministrador
    : userRol === "Admin";
});
const showTheUserMenu = computed(() => {
  return userLogged.value.email && !userLoggedIsExternal.value;
});
const showExternalLoggedUser = computed(() => {
  return userLogged.value.email && userLoggedIsExternal.value;
});
const userLogged = computed(() => authorizationModelRef.value);
const userLoggedIsExternal = ref(false);
const userRol = authorizationModelRef.value.roles[0];
const impersionationApi = ref([]);
const selectedImpersonation = ref(null);
const showSelectedImpersonation = computed(
  () =>
    selectedImpersonation.value != null &&
    selectedImpersonation.value != "" &&
    typeof selectedImpersonation.value == "object"
);
// #endregion

// #region Functions
onMounted(async () => {
  if ((await isLogged()) && userRol == "Admin") {
    getImpersonationUsers();
  }

  if ((await getMsalToken()) == null) {
    userLoggedIsExternal.value = true;
  }

  const impersonationToken = localStorage.getItem("impersonationToken");

  if (impersonationToken) {
    selectedImpersonation.value = JSON.parse(impersonationToken);
  }
});

function changeLayout() {
  let newLayout = layout.value == "admin" ? "web" : "admin";

  const currentRoute = router.currentRoute.value;

  if (currentRoute.name == "Public reports") {
    newLayout = "admin";
  }

  store.commit("setLayout", newLayout);
  localStorage.setItem("layout", newLayout);

  if (newLayout == "web") {
    router.push({
      name: "Public reports",
    });
  } else {
    router.push({
      name: "Reports",
    });
  }
}

function handleGoLogin() {
  router.push({
    name: "Login",
  });
}

async function getImpersonationUsers(event = null) {
  impersionationApi.value = await appUserListAll({
    fromImpersonation: true,
    email: event?.query ? event.query : null,
  });
}

function clearImpersonation() {
  localStorage.removeItem("impersonationToken");
  window.location.reload();
}
// #endregion

// #region Effects, Hooks
watch(selectedImpersonation, (newValue) => {
  if (newValue) {
    if (typeof newValue === "string") {
      return;
    }

    const impersonationToken = localStorage.getItem("impersonationToken");

    if (!impersonationToken) {
      localStorage.setItem("impersonationToken", JSON.stringify(newValue));
      window.location.pathname = "/informes";
    }
  }
});

watch(triggerImpersonationUser, (newValue) => {
  if (newValue) {
    impersionationApi.value.forEach((impersonationItem) => {
      if (impersonationItem.idUser == newValue.idUser) {
        selectedImpersonation.value = impersonationItem;
      }
    });
  }
});
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss">
@import "@/styles/_vars.scss";

.navbar-section {
  background-color: $primary;

  .logo-container {
    grid-area: logo;
    aspect-ratio: 160 / 50; // Measured aspect ratio of the logo
    height: 3rem;

    .logo {
      width: 100%;
      height: 100%;
      background-image: url("@/assets/images/logo-2.svg");
      background-size: contain;
      background-repeat: no-repeat;
      background-position: left center;
    }
  }

  .the-user-menu {
    flex: unset;
    height: auto;

    .change-panel {
      align-items: center;
      display: flex;
      font-size: 12px;
      font-weight: 400;
      margin: 0;
      max-width: 100%;
      gap: 0.5rem;
      margin-top: 0.2rem;
      cursor: pointer;

      i {
        color: #89a1ac;
        font-size: 12px;
      }
    }

    .impersonation-dropdown {
      width: 100%;
      margin-top: 0.5rem;

      .p-dropdown-label,
      .p-autocomplete-input {
        padding: 0.2rem 0.5rem;
        font-size: 0.8rem;
      }

      .p-dropdown-trigger {
        width: 2rem;

        .p-icon {
          width: 0.8rem;
          height: 0.8rem;
        }
      }

      .p-autocomplete-dropdown {
        width: 2rem;
        padding: 0;

        .p-icon {
          width: 0.8rem;
          height: 0.8rem;
        }
      }
    }
  }

  .impersonation-button {
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;

    .p-button-icon {
      font-size: 0.8rem;
    }
  }
}
</style>
